import { NavLink } from 'react-router-dom'
import {
  LayoutDashboard,
  Package,
  FolderOpen,
  FileText,
  Image,
  Award,
  Settings,
  MessageSquare,
  Mail,
  SlidersHorizontal,
  Layers,
  Info,
  Phone,
  ShoppingBag,
  ChevronDown,
  Heart,
  MapPin,
  Building2,
  BookOpen,
  Download,
  Leaf,
  HardDrive,
  MessageCircle,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useState } from 'react'

interface NavGroup {
  label: string
  items: NavItem[]
  defaultOpen?: boolean
}

interface NavItem {
  to: string
  icon: React.ElementType
  label: string
  end?: boolean
}

const navGroups: NavGroup[] = [
  {
    label: 'Overview',
    defaultOpen: true,
    items: [
      { to: '/admin', icon: LayoutDashboard, label: 'Dashboard', end: true },
    ],
  },
  {
    label: 'Page Contents',
    defaultOpen: true,
    items: [
      { to: '/admin/homepage', icon: Layers, label: 'Homepage' },
      { to: '/admin/pages/about', icon: Info, label: 'About Page' },
      { to: '/admin/pages/sustainability', icon: Leaf, label: 'Sustainability Page' },
      { to: '/admin/pages/products', icon: ShoppingBag, label: 'Products Page' },
      { to: '/admin/pages/gallery', icon: Image, label: 'Gallery Page' },
      { to: '/admin/pages/certificates', icon: Award, label: 'Certificates Page' },
      { to: '/admin/pages/contact', icon: Phone, label: 'Contact Page' },
    ],
  },
  {
    label: 'Products & Catalog',
    defaultOpen: true,
    items: [
      { to: '/admin/products', icon: Package, label: 'Products' },
      { to: '/admin/categories', icon: FolderOpen, label: 'Categories' },
      { to: '/admin/configurators', icon: SlidersHorizontal, label: 'Configurators' },
      { to: '/admin/catalogs', icon: BookOpen, label: 'PDF Catalogs' },
    ],
  },
  {
    label: 'Company Info',
    defaultOpen: true,
    items: [
      { to: '/admin/company-values', icon: Heart, label: 'Values' },
      { to: '/admin/company-locations', icon: MapPin, label: 'Locations' },
      { to: '/admin/certifications', icon: Award, label: 'Certifications' },
    ],
  },
  {
    label: 'Media & Content',
    defaultOpen: true,
    items: [
      { to: '/admin/gallery', icon: Image, label: 'Gallery Images' },
      { to: '/admin/certificates', icon: Building2, label: 'Certificate Files' },
      { to: '/admin/file-manager', icon: HardDrive, label: 'File Manager' },
    ],
  },
  {
    label: 'Inquiries',
    defaultOpen: true,
    items: [
      { to: '/admin/quotes', icon: FileText, label: 'Quote Requests' },
      { to: '/admin/catalog-requests', icon: Download, label: 'Catalog Requests' },
      { to: '/admin/messages', icon: MessageSquare, label: 'Messages' },
      { to: '/admin/live-chat', icon: MessageCircle, label: 'Live Chat' },
      { to: '/admin/subscribers', icon: Mail, label: 'Subscribers' },
    ],
  },
  {
    label: 'Settings',
    defaultOpen: true,
    items: [
      { to: '/admin/settings', icon: Settings, label: 'Site Settings' },
    ],
  },
]

function NavGroupComponent({ group }: { group: NavGroup }) {
  const [isOpen, setIsOpen] = useState(group.defaultOpen ?? true)

  return (
    <div className="mb-2">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-secondary-500 uppercase tracking-wider hover:text-secondary-300 transition-colors"
      >
        {group.label}
        <ChevronDown className={cn('w-4 h-4 transition-transform', isOpen && 'rotate-180')} />
      </button>
      {isOpen && (
        <div className="space-y-0.5">
          {group.items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-primary-600 text-white'
                    : 'text-secondary-300 hover:bg-secondary-800 hover:text-white'
                )
              }
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  )
}

export function AdminSidebar() {
  return (
    <aside className="fixed left-0 top-0 bottom-0 w-64 bg-secondary-900 text-white hidden lg:flex flex-col">
      {/* Logo */}
      <div className="px-5 py-4 border-b border-secondary-800">
        <a href="/" className="block">
          <span className="text-3xl font-bold text-primary-400">Engitronix</span>
        </a>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 overflow-y-auto">
        {navGroups.map((group) => (
          <NavGroupComponent key={group.label} group={group} />
        ))}
      </nav>

      {/* Preview Links */}
      <div className="p-4 border-t border-secondary-800">
        <p className="text-xs text-secondary-500 uppercase tracking-wider mb-2">Preview</p>
        <div className="flex flex-wrap gap-2">
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-secondary-400 hover:text-white transition-colors px-2 py-1 bg-secondary-800 rounded"
          >
            Home
          </a>
          <a
            href="/products"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-secondary-400 hover:text-white transition-colors px-2 py-1 bg-secondary-800 rounded"
          >
            Products
          </a>
          <a
            href="/about"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-secondary-400 hover:text-white transition-colors px-2 py-1 bg-secondary-800 rounded"
          >
            About
          </a>
          <a
            href="/contact"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-secondary-400 hover:text-white transition-colors px-2 py-1 bg-secondary-800 rounded"
          >
            Contact
          </a>
        </div>
      </div>
    </aside>
  )
}
